let FirebaseInstance = null;

class Firebase {
  constructor({ auth, db, storage, analytics, functions, firebase } = {}) {
    this.authUser = {};
    this.auth = auth;
    this.db = db;
    this.storage = storage;
    this.analytics = analytics;
    this.functions = functions;
    this.firebase = firebase;

    if (this.auth && this.auth.onAuthStateChanged) {
      this.auth.onAuthStateChanged(user => {
        this.authUser = { user };
      });
    }

    if (this.auth && this.auth.onUserChanged) {
      this.auth.onUserChanged(user => {
        this.authUser = { user };
      });
    }

    FirebaseInstance = this;

    return FirebaseInstance;
  }

  static getInstance() {
    return (
      FirebaseInstance || new Firebase()
      // TODO: Align with Archie on this
      // (FirebaseInstance && Object.freeze(FirebaseInstance)) || new Firebase()
    );
  }

  checkAuthorization() {
    if (!this.auth.currentUser) {
      return ['[error/unauthorized] You are unauthorized to perform any operation. Please login and try again.'];
    }

    return [];
  }

  getFBCollectionData({ endCollection, parentCollection = 'users', parentDoc } = {}) {
    const [authError] = this.checkAuthorization();

    if (authError) {
      return [authError];
    }

    return this.db
      .collection(parentCollection)
      .doc(parentDoc || this.auth.currentUser.uid)
      .collection(endCollection);
  }

  getFBCollectionWhere({ parentCollection = 'users', key, value } = {}) {
    const [authError] = this.checkAuthorization();

    if (authError) {
      return [authError];
    }

    return this.db.collection(parentCollection).where(key, '==', value);
  }

  getFBCollectionDataWhereBool({ endCollection, key, value, parentCollection = 'users', parentDoc } = {}) {
    const [authError] = this.checkAuthorization();

    if (authError) {
      return [authError];
    }

    return this.db
      .collection(parentCollection)
      .doc(parentDoc || this.auth.currentUser.uid)
      .collection(endCollection)
      .where(key, '==', value);
  }

  addFBData({ doc, values, endCollection, parentCollection = 'users' } = {}) {
    const [authError] = this.checkAuthorization();

    if (authError) {
      return [authError];
    }

    return this.db
      .collection(parentCollection)
      .doc(doc || this.auth.currentUser.uid)
      .collection(endCollection)
      .add(values);
  }

  updateFBData({ values, doc, endCollection, parentCollection = 'users' } = {}) {
    const { id } = values;
    const [authError] = this.checkAuthorization();

    if (authError) {
      return [authError];
    }

    const clone = { ...values };
    delete clone.id;

    return this.db
      .collection(parentCollection)
      .doc(doc || this.auth.currentUser.uid)
      .collection(endCollection)
      .doc(id)
      .update({
        ...clone
      });
  }

  addFBDoc({ parentCollection = 'users', docData = {} } = {}) {
    const [authError] = this.checkAuthorization();

    if (authError) {
      return [authError];
    }

    return this.db
      .collection(parentCollection)
      .add(docData)
      .then(res => [null, res])
      .catch(err => [err]);
  }

  setFBDoc({ parentCollection = 'users', doc, docData = {} } = {}) {
    const [authError] = this.checkAuthorization();

    if (authError) {
      return [authError];
    }

    return this.db
      .collection(parentCollection)
      .doc(doc)
      .set({
        ...docData
      })
      .then(res => [null, res])
      .catch(err => [err]);
  }

  getFBDoc({ parentCollection = 'users', doc } = {}) {
    const [authError] = this.checkAuthorization();

    if (authError) {
      return [authError];
    }

    const collectionDoc = doc ? doc : this.auth.currentUser.uid;

    return this.db.collection(parentCollection).doc(collectionDoc);
  }

  deleteFBDoc({ parentCollection, doc } = {}) {
    const [authError] = this.checkAuthorization();

    if (authError) {
      return [authError];
    }

    return this.db
      .collection(parentCollection)
      .doc(doc)
      .delete()
      .then(res => [null, res])
      .catch(err => [err]);
  }

  updateFBDoc({ doc, docData, parentCollection = 'users' } = {}) {
    const [authError] = this.checkAuthorization();

    if (authError) {
      return [authError];
    }

    return this.db
      .collection(parentCollection)
      .doc(this.auth.currentUser.uid)
      .update({
        [doc]: docData
      });
  }

  updateMultipleFBDoc({ docs = {}, parentCollection = 'users' } = {}) {
    const [authError] = this.checkAuthorization();

    if (authError) {
      return [authError];
    }

    return this.db
      .collection(parentCollection)
      .doc(this.auth.currentUser.uid)
      .update({
        ...docs
      });
  }

  getFBData({ id, parentDoc, endCollection, parentCollection = 'users' } = {}) {
    const [authError] = this.checkAuthorization();

    if (authError) {
      return [authError];
    }

    let collection = this.db
      .collection(parentCollection)
      .doc(parentDoc || `${this.auth.currentUser.uid}`)
      .collection(endCollection);

    if (id) {
      return collection.doc(id);
    }

    return collection.doc();
  }

  async deleteMultipleFBData({ ids, doc, endCollection, parentCollection = 'users' } = {}) {
    const [authError] = this.checkAuthorization();

    if (authError) {
      return [authError];
    }

    let batch = this.db.batch();
    if (ids && ids.length > 0) {
      ids.forEach(id => {
        let dataRef = this.db
          .collection(parentCollection)
          .doc(doc || this.auth.currentUser.uid)
          .collection(endCollection)
          .doc(id);
        batch.delete(dataRef);
      });
      batch.commit();
    }
  }
}

export const FirebaseService = Firebase.getInstance();
export default Firebase;
